import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFunnel } from '../funnel/useFunnel'
import { routeFor } from '../funnel/useFunnel'
import { consentSettled } from '../funnel/consent'
import { eventOnce, funnelApi, initialSessionId, type Answer } from '../lib/api'

/**
 * Personalization theatre: reads their own answers back while the result is
 * generated. The delay is the point — the reflect-back is what makes the result
 * on the next screen feel built rather than looked up.
 *
 * Requesting the result here is also what sets `resultViewedAt` server-side,
 * which the claim path requires. That has to happen before the email gate.
 */
export function CraftingScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { answers } = useFunnel()
  const [line, setLine] = useState(0)

  const lines = useMemo(() => {
    const label = (key: string, value: Answer | undefined) =>
      value === undefined || Array.isArray(value) ? '' : t(`questions.${key}.options.${String(value)}`)
    const boundaries = Array.isArray(answers.topic_boundaries)
      ? answers.topic_boundaries.filter((value) => value !== 'prefer_not_to_say')
      : []
    return [
      t('crafting.goal', { value: label('connection_goal', answers.connection_goal) }),
      t('crafting.context', { value: label('connection_context', answers.connection_context) }),
      t('crafting.energy', { value: label('social_energy', answers.social_energy) }),
      boundaries.length
        ? t('crafting.boundaries', { count: boundaries.length })
        : t('crafting.depth', { value: label('preferred_depth', answers.preferred_depth) }),
    ].filter((text) => !text.endsWith(' — ') && text.trim().length > 0)
  }, [answers, t])

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const interval = reduced ? 25 : 650
    const total = reduced ? 120 : lines.length * interval + 500
    const ticker = window.setInterval(
      () => setLine((value) => Math.min(value + 1, lines.length - 1)),
      interval
    )
    const done = window.setTimeout(async () => {
      try {
        // Chained off consent so a very fast run can't race the agreement write.
        await consentSettled()
        await queryClient.prefetchQuery({
          queryKey: ['funnel-result', initialSessionId],
          queryFn: () => funnelApi.result(initialSessionId),
        })
      } finally {
        void eventOnce(initialSessionId, 'crafting_completed').catch(() => {})
        navigate(routeFor('gate'), { replace: true })
      }
    }, total)
    return () => {
      clearInterval(ticker)
      clearTimeout(done)
    }
  }, [lines.length, navigate, queryClient])

  return (
    <div className="content-column text-center">
      <div className="crafting-art" aria-hidden>
        <span className="craft-dot one" />
        <span className="craft-dot two" />
        <span className="craft-heart">♥</span>
      </div>
      <p className="eyebrow">{t('crafting.eyebrow')}</p>
      <h1 className="value-title">{t('crafting.title')}</h1>
      <p className="body-copy">{t('crafting.body')}</p>
      <div className="craft-progress">
        <i style={{ width: `${((line + 1) / lines.length) * 100}%` }} />
      </div>
      <div className="craft-lines" aria-live="polite">
        {lines.map((text, index) => (
          <p key={text} className={index <= line ? 'done' : ''}>
            <span aria-hidden>{index < line ? '✓' : index === line ? '●' : '○'}</span>
            {text}
          </p>
        ))}
      </div>
    </div>
  )
}
